import http from "@/api/http";

/**
 * Kelo AI assistant widget (RCS-148), the floating assistant the Intranet
 * also carries (INT-500). The widget script boots once when it loads: it
 * reads `window.KeloConfig`, asks `getSignature()` for a signed identity
 * before every call it makes to Kelo, and adds `#kelo-widget-host` to
 * <body>. It has no teardown of its own, and this SPA signs users out and
 * in again without a page load, so mounting and unmounting it lives here.
 *
 * The widget keeps its chats in localStorage under "kelo_widget:<assistant>:…",
 * not per user. The history is tagged with the user it belongs to and wiped
 * on sign-out, or when someone else signs in on this browser, so nobody sees
 * another person's conversations.
 */

export const KELO_WIDGET_SRC = "https://cdn.kelo.co.ke/widget.js";
export const KELO_HOST_ID = "kelo-widget-host";
export const KELO_SCRIPT_ID = "kelo-widget-script";
const STORAGE_PREFIX = "kelo_widget:";
const OWNER_KEY = "rac:kelo-owner";

export interface KeloSignature {
  assistant_id: string;
  timestamp: number;
  signature: string;
  identity: string;
}

declare global {
  interface Window {
    KeloConfig?: {
      assistantId: string;
      getSignature: () => Promise<KeloSignature>;
    };
  }
}

// Bumped on every unmount, so a widget booted for an earlier sign-in can no
// longer get a signature and anything still in flight for it is dropped.
let generation = 0;
let mountedFor: string | null = null;

const fetchSignature = async (): Promise<KeloSignature> => {
  const response = await http().get("kelo/signature");
  return response.data.data;
};

// A widget left over from a signed-out session (still booting, or polling
// for a document) gets no signature, so it can't talk to Kelo as that user.
const signerFor = (run: number) => async (): Promise<KeloSignature> => {
  if (run === generation) {
    const signature = await fetchSignature();
    if (run === generation) return signature;
  }
  throw new Error("[Kelo] The user has signed out.");
};

const storageKeys = (storage: Storage): string[] => {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
  }
  return keys;
};

const clearHistory = () => {
  for (const storage of [localStorage, sessionStorage]) {
    try {
      storageKeys(storage).forEach((key) => storage.removeItem(key));
    } catch {
      // Storage blocked (private mode, site data off): nothing was kept.
    }
  }
};

// Keeps the stored chats only if they are this user's own.
const claimHistory = (userId: string) => {
  try {
    if (localStorage.getItem(OWNER_KEY) !== userId) clearHistory();
    localStorage.setItem(OWNER_KEY, userId);
  } catch {
    clearHistory();
  }
};

/**
 * Shows the assistant for the signed-in user. Does nothing when Kelo is not
 * set up on the API (503), the account is not active (403) or the API is
 * unreachable: the assistant is an extra, never a reason for an error.
 */
export async function mountKelo(userId: string): Promise<void> {
  if (mountedFor === userId) return;
  if (mountedFor !== null) unmountKelo();

  mountedFor = userId;
  const run = generation;

  let signature: KeloSignature;
  try {
    signature = await fetchSignature();
  } catch {
    return;
  }
  if (run !== generation) return;

  claimHistory(userId);
  // One that finished booting after its user signed out.
  document.getElementById(KELO_HOST_ID)?.remove();

  window.KeloConfig = {
    assistantId: signature.assistant_id,
    getSignature: signerFor(run),
  };

  const script = document.createElement("script");
  script.id = KELO_SCRIPT_ID;
  script.src = KELO_WIDGET_SRC;
  script.async = true;
  document.body.appendChild(script);
}

/** Takes the assistant off the page; the stored chats stay for the same user. */
export function unmountKelo(): void {
  generation++;
  mountedFor = null;
  document.getElementById(KELO_HOST_ID)?.remove();
  document.getElementById(KELO_SCRIPT_ID)?.remove();
  delete window.KeloConfig;
}

/** On sign-out: also forgets the chats this browser kept. */
export function forgetKelo(): void {
  unmountKelo();
  clearHistory();
  try {
    localStorage.removeItem(OWNER_KEY);
  } catch {
    // Nothing stored.
  }
}
